import { z } from 'zod';

export type DiagnosticValueClass =
  | 'string'
  | 'number'
  | 'bigint'
  | 'boolean'
  | 'symbol'
  | 'function'
  | 'undefined'
  | 'object';

const primitiveClasses: Partial<Record<z.ZodParsedType, DiagnosticValueClass>> = {
  string: 'string',
  number: 'number',
  nan: 'number',
  bigint: 'bigint',
  boolean: 'boolean',
  symbol: 'symbol',
  function: 'function',
  undefined: 'undefined',
};

/** Preserve diagnostic categories without retaining malformed field contents. */
export function diagnosticValueClass(parsedType: z.ZodParsedType): DiagnosticValueClass {
  return primitiveClasses[parsedType] ?? 'object';
}

export interface DiagnosticScheduleField {
  value: string | undefined;
  fingerprint: string;
}
export interface DiagnosticSchedule {
  eventId: DiagnosticScheduleField;
  tradingStartAt: DiagnosticScheduleField;
  tradingEndAt: DiagnosticScheduleField;
}
const missingField: DiagnosticScheduleField = { value: undefined, fingerprint: 'undefined' };
const scheduleFieldSchema: z.ZodType<DiagnosticScheduleField, z.ZodTypeDef, unknown> = z
  .string()
  .transform((value): DiagnosticScheduleField => ({ value, fingerprint: value }))
  .catch(({ error }): DiagnosticScheduleField => {
    const issue = error.issues[0];
    return {
      value: undefined,
      fingerprint:
        issue?.code === 'invalid_type' ? diagnosticValueClass(issue.received) : 'undefined',
    };
  });

export const diagnosticScheduleSchema: z.ZodType<DiagnosticSchedule, z.ZodTypeDef, unknown> = z
  .object({
    eventId: scheduleFieldSchema,
    tradingStartAt: scheduleFieldSchema,
    tradingEndAt: scheduleFieldSchema,
  })
  .catch({ eventId: missingField, tradingStartAt: missingField, tradingEndAt: missingField });

// Row contents are irrelevant to diagnostics; validate only the container in constant time.
export const populatedCatalogSchema: z.ZodType<{ data: { readonly length: number } }> = z.object({
  data: z.custom<{ readonly length: number }>(Array.isArray).refine((rows) => rows.length > 0),
});
